import React from "react";
import { Typography, Skeleton, Space } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import {
  SummaryCard,
  SummaryHeader,
  SummaryTitle,
  SummaryDescription,
  ProfitSection,
  ProfitAmount,
  MarginBadge,
  MarginText,
  MetricsGrid,
  MetricCard,
  MetricHeader,
  MetricIcon,
  MetricTitle,
  MetricValue,
  DateSelectorWrapper,
} from "./styles";
import {
  addCommasInPrices,
  generateKeyMetrics,
  getDefaultKeyMetrics,
  SUMMARY_TEXTS,
  TRENDING_UP_ICON_CONFIG,
} from "./constants";
import { ProfitSummaryProps } from "./types";
import DateSelector from "../../../commonComponents/DateSelector";

const { Text } = Typography;

const ProfitSummary: React.FC<ProfitSummaryProps> = ({
  selectedDateRange,
  onDateRangeChange,
}) => {
  const { profitData, loading } = useSelector(
    (state: RootState) => state.profitMargins
  );

  const summary = profitData?.summary;
  const totalProfit = summary?.grossProfit || 0;
  const totalMargin = summary?.profitMargin || 0;
  const keyMetrics = summary
    ? generateKeyMetrics(summary)
    : getDefaultKeyMetrics();

  return (
    <SummaryCard>
      <SummaryHeader>
        <SummaryTitle>{SUMMARY_TEXTS.title}</SummaryTitle>
        <DateSelectorWrapper>
          <DateSelector onDateSelect={onDateRangeChange} />
        </DateSelectorWrapper>
      </SummaryHeader>

      <SummaryDescription>{SUMMARY_TEXTS.description}</SummaryDescription>

      <ProfitSection>
        <ProfitAmount>
          ${addCommasInPrices(parseFloat(totalProfit.toString()).toFixed(2))}
        </ProfitAmount>
        <MarginBadge>
          <FontAwesomeIcon
            icon={TRENDING_UP_ICON_CONFIG.icon}
            style={{
              color: TRENDING_UP_ICON_CONFIG.color,
              fontSize: TRENDING_UP_ICON_CONFIG.fontSize,
            }}
          />
          <MarginText>
            {parseFloat(totalMargin.toString()).toFixed(2)}%{" "}
            {SUMMARY_TEXTS.marginLabel}
          </MarginText>
        </MarginBadge>
      </ProfitSection>

      {loading ? (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Skeleton active paragraph={{ rows: 2 }} />
          <MetricsGrid>
            {Array.from({ length: 6 }).map((_, index) => (
              <MetricCard key={index}>
                <Skeleton active paragraph={{ rows: 2 }} />
              </MetricCard>
            ))}
          </MetricsGrid>
        </Space>
      ) : (
        <>
          <Text
            strong
            style={{ fontSize: "18px", marginBottom: "16px", display: "block" }}
          >
            {SUMMARY_TEXTS.keyMetricsTitle}
          </Text>
          <MetricsGrid>
            {keyMetrics.map((metric) => (
              <MetricCard key={metric.id} hoverable>
                <MetricHeader>
                  <MetricIcon color={metric.color}>
                    <FontAwesomeIcon icon={metric.icon} />
                  </MetricIcon>
                  <MetricTitle>{metric.title}</MetricTitle>
                </MetricHeader>
                <MetricValue>{metric.value}</MetricValue>
              </MetricCard>
            ))}
          </MetricsGrid>
        </>
      )}
    </SummaryCard>
  );
};

export default ProfitSummary;
